import { ReactNode } from 'react';
import { Content, Page } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/frontend-plugin-api';
import { makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { Hero } from './components/Hero';
import { ToolGrid } from './components/ToolGrid';
import { CatalogPulse } from './components/CatalogPulse';
import { QuickActions } from './components/QuickActions';
import { WorkflowStarters } from './components/WorkflowStarters';

const useStyles = makeStyles({
  shell: {
    maxWidth: 1400,
    margin: '0 auto',
    width: '100%',
    display: 'grid',
    gap: 24,
  },
  // Asymmetric split — the tool grid carries more weight than the rail.
  split: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.9fr) minmax(280px, 1fr)',
    gap: 24,
    alignItems: 'start',
    '@media (max-width: 1080px)': { gridTemplateColumns: '1fr' },
  },
  rail: { display: 'grid', gap: 24 },
  panel: {
    padding: 24,
    borderRadius: 14,
    border: '1px solid var(--q-border)',
    background: 'var(--q-bg2)',
  },
  panelHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
  },
  panelTitle: {
    fontFamily: 'var(--q-font-display)',
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: '-0.01em',
    color: 'var(--q-text)',
    margin: 0,
  },
  sectionHead: { marginBottom: 4 },
});

const Panel = ({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <section className={classes.panel}>
      <div className={classes.panelHead}>
        <h2 className={classes.panelTitle}>{title}</h2>
        {eyebrow && <span className="q-eyebrow">{eyebrow}</span>}
      </div>
      {children}
    </section>
  );
};

export const QuodinHome = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [firstName, setFirstName] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const name = profile.displayName?.split(' ')[0];
        if (!cancelled) setFirstName(name);
      } catch {
        // Greeting is decorative; an anonymous session simply omits the name.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return (
    <Page themeId="home">
      <Content>
        <div className={classes.shell}>
          <Hero userName={firstName} />

          <div className={classes.split}>
            <div>
              <div className={classes.sectionHead}>
                <span className="q-eyebrow">AI Workspace</span>
              </div>
              <p
                style={{
                  color: 'var(--q-sub)',
                  fontSize: 14,
                  lineHeight: 1.6,
                  margin: '8px 0 18px',
                  maxWidth: '62ch',
                }}
              >
                Three services do the heavy lifting behind Quodin. Status is
                probed live through the backend proxy every 30 seconds.
              </p>
              <ToolGrid />

              <div style={{ marginTop: 28 }}>
                <Panel title="Workflow starters" eyebrow="Recipes">
                  <WorkflowStarters />
                </Panel>
              </div>
            </div>

            <div className={classes.rail}>
              <Panel title="Catalog" eyebrow="Live">
                <CatalogPulse />
              </Panel>
              <Panel title="Jump in">
                <QuickActions />
              </Panel>
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};
